import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import { authApi } from '../../lib/auth';

type ResetMethod = 'email' | 'sms';

export default function ForgotPassword() {
  const [method, setMethod] = useState<ResetMethod>('email');

  // 이메일 방식 상태
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // SMS 방식 상태
  const [phone, setPhone] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 공통 상태
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // SMS 재발송 카운트다운
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [countdown]);

  // 휴대폰 번호 포맷팅
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  // 이메일 방식 - 재설정 링크 발송
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApi.requestPasswordReset(email);
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // SMS 방식 - 인증 코드 발송
  const handleSmsRequest = async () => {
    setError('');
    setLoading(true);

    try {
      await authApi.requestPasswordResetBySms(phone);
      setSmsSent(true);
      setCountdown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : '인증 코드 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return '비밀번호는 8자 이상이어야 합니다.';
    }
    if (!/[a-zA-Z]/.test(password)) {
      return '비밀번호에 영문자가 포함되어야 합니다.';
    }
    if (!/\d/.test(password)) {
      return '비밀번호에 숫자가 포함되어야 합니다.';
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return '비밀번호에 특수문자가 포함되어야 합니다.';
    }
    return null;
  };

  // SMS 방식 - 비밀번호 변경
  const handleSmsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // SMS 발송 전이면 SMS 발송 먼저 실행
    if (!smsSent) {
      if (phone && !loading && countdown === 0) {
        handleSmsRequest();
      }
      return;
    }

    // 유효성 검사
    if (smsCode.length !== 6) {
      setError('인증 코드는 6자리입니다.');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);

    try {
      await authApi.verifyPasswordResetBySms(phone, smsCode, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '비밀번호 재설정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 성공 화면
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-card shadow p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-ink-900 mb-2">비밀번호 변경 완료</h2>
            <p className="text-ink-600 mb-6">
              새 비밀번호로 로그인해주세요.
            </p>
            <Link
              to="/login"
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-control shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
            >
              로그인하기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 이메일 발송 완료 화면
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-card shadow p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-ink-900 mb-2">재설정 링크 발송 완료</h2>
            <p className="text-ink-600 mb-4">
              입력하신 이메일로 비밀번호 재설정 링크가 발송되었습니다.
            </p>
            <p className="text-sm text-ink-500 mb-6">
              이메일의 링크를 클릭하여 비밀번호를 변경해주세요.
            </p>
            <Link
              to="/login"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              로그인으로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">비밀번호 재설정</h2>
          <p className="mt-2 text-sm text-ink-600">
            비밀번호를 재설정할 방법을 선택하세요.
          </p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 방법 선택 탭 */}
        <Card padding="none" radius="lg" elevation="md">
          <div className="border-b border-ink-200">
            <nav className="-mb-px flex">
              <button
                type="button"
                onClick={() => setMethod('email')}
                className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  method === 'email'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
                }`}
              >
                이메일로 재설정
              </button>
              <button
                type="button"
                onClick={() => setMethod('sms')}
                className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  method === 'sms'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
                }`}
              >
                SMS로 재설정
              </button>
            </nav>
          </div>

          <div className="p-6">
            {method === 'email' ? (
              /* 이메일 방식 */
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-card p-3">
                  <p className="text-sm text-blue-800">
                    등록된 이메일로 비밀번호 재설정 링크가 발송됩니다.
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ink-700">
                    이메일 주소
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="user@example.com"
                    className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  />
                </div>

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? '발송 중...' : '재설정 링크 발송'}
                </Button>
              </form>
            ) : (
              /* SMS 방식 */
              <form onSubmit={handleSmsSubmit} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-card p-3">
                  <p className="text-sm text-blue-800">
                    등록된 휴대폰 번호로 인증 코드가 발송됩니다.
                  </p>
                </div>

                {/* 휴대폰 번호 입력 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-ink-700">
                    휴대폰 번호
                  </label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="tel"
                      inputMode="numeric"
                      id="phone"
                      value={phone}
                      onChange={handlePhoneChange}
                      disabled={smsSent}
                      required
                      placeholder="010-1234-5678"
                      maxLength={13}
                      className="flex-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm disabled:bg-ink-100"
                    />
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleSmsRequest}
                      disabled={loading || countdown > 0 || !phone}
                      className="whitespace-nowrap"
                    >
                      {loading ? '발송 중...' : countdown > 0 ? `${countdown}초` : smsSent ? '재발송' : '인증 코드 발송'}
                    </Button>
                  </div>
                </div>

                {smsSent && (
                  <>
                    {/* 인증 코드 입력 */}
                    <div>
                      <label htmlFor="smsCode" className="block text-sm font-medium text-ink-700">
                        인증 코드
                      </label>
                      <input
                        type="text"
                        id="smsCode"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        placeholder="6자리 숫자"
                        maxLength={6}
                        className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm font-mono text-center text-lg tracking-widest"
                      />
                    </div>

                    {/* 새 비밀번호 입력 */}
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-ink-700">
                        새 비밀번호
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder="8자 이상, 영문/숫자/특수문자 조합"
                        className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                      />
                    </div>

                    {/* 새 비밀번호 확인 */}
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700">
                        새 비밀번호 확인
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder="새 비밀번호를 다시 입력하세요"
                        className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                      />
                    </div>

                    <Button type="submit" variant="primary" disabled={loading} className="w-full">
                      {loading ? '처리 중...' : '비밀번호 변경'}
                    </Button>
                  </>
                )}
              </form>
            )}
          </div>
        </Card>

        {/* 로그인으로 돌아가기 */}
        <div className="text-center">
          <Link
            to="/login"
            className="text-sm text-ink-600 hover:text-ink-900"
          >
            로그인으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}
